<template>
  <!-- Infinite scroll detector - emits events when the user scrolls to the bottom of the page -->
  <infinite-scroll-detector
    class="duplicates-tab-duplicates"
    @load-more="onLoadMore"
  >
    <!-- Filters (Auto Resolved / Reviewed) that are applied to the duplicate groups -->
    <duplicates-filters
      v-if="!hasNoDuplicatesToShow"
      :selected.sync="selectedFilters"
      :review="review"
      class="mb-6"
    />

    <!-- Skeleton loader shown when the duplicate groups are loading -->
    <duplicate-group-skeleton-loader v-if="showLoadingState" />

    <!-- Duplicate groups shown when the duplicate groups are loaded -->
    <duplicate-group-card-list
      v-else
      :duplicate-groups="duplicateGroups"
      :total-count="listTotalCount"
      empty-state-title="No duplicates"
      :empty-state-message="emptyStateMessage"
      :hide-back-button="!showBackButton"
      @mark-as-not-duplicate="onMarkAsNotDuplicate"
      @mark-as-duplicate="onMarkAsDuplicate"
      @go-back="$emit('go-back')"
    />

    <!-- Loading more indicator shown when the user scrolls to the bottom of the page -->
    <loading-more-indicator
      v-if="showLoadingIndicator"
      :loading="loadingMore"
      :has-next-page="hasMoreDuplicateGroups"
      @load-more="onLoadMore"
    />
  </infinite-scroll-detector>
</template>

<script>
import InfiniteScrollDetector from '@/components/Common/InfiniteScrollDetector';
import LoadingMoreIndicator from '@/components/Common/LoadingMoreIndicator';
import DuplicatesFilters from './DuplicatesFilters';
import DuplicateGroupSkeletonLoader from './DuplicateGroupSkeletonLoader';
import DuplicateGroupCardList from './DuplicateGroupCardList';
import { Review, ReferenceDuplicateGroupConnection } from '@/models';
import { DuplicateFilterBy } from '@/enums';
import {
  GetReferenceDuplicateGroups,
  GetReferenceDuplicateGroupsAutoResolved,
  GetReferenceDuplicateGroupsResolvedByUser,
  GetReferenceDuplicateGroupsReviewNeeded,
} from '@/graphql';
import { apolloClient } from '@/plugins/apollo';
import { appendNextResults } from '@/modules/paginationHelpers';
import { mapActions } from 'vuex';

const PAGE_SIZE = 10;

export default {
  name: 'DuplicatesTabDuplicates',
  components: {
    InfiniteScrollDetector,
    LoadingMoreIndicator,
    DuplicatesFilters,
    DuplicateGroupSkeletonLoader,
    DuplicateGroupCardList,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['go-back'],
  data: () => ({
    selectedFilters: [
      DuplicateFilterBy.AUTO_RESOLVED,
      DuplicateFilterBy.REVIEWED,
    ],
    loadingMore: false,
    allDuplicateGroups: null,
    autoResolvedDuplicateGroups: null,
    reviewedDuplicateGroups: null,
    reviewNeededDuplicateGroups: null,
  }),
  apollo: {
    allDuplicateGroups: {
      query: GetReferenceDuplicateGroups,
      variables() {
        return {
          reviewId: this.review.id,
          first: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceDuplicateGroupConnection(
          data.referenceDuplicateGroups
        ),
      skip() {
        return this.selectedFilters.length < 2;
      },
    },
    autoResolvedDuplicateGroups: {
      query: GetReferenceDuplicateGroupsAutoResolved,
      variables() {
        return {
          reviewId: this.review.id,
          first: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceDuplicateGroupConnection(
          data.referenceDuplicateGroupsAutoResolved
        ),
      skip() {
        return !this.showAutoResolvedOnly;
      },
    },
    reviewedDuplicateGroups: {
      query: GetReferenceDuplicateGroupsResolvedByUser,
      variables() {
        return {
          reviewId: this.review.id,
          first: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceDuplicateGroupConnection(
          data.referenceDuplicateGroupsResolvedByUser
        ),
      skip() {
        return !this.showReviewedOnly;
      },
    },
    reviewNeededDuplicateGroups: {
      query: GetReferenceDuplicateGroupsReviewNeeded,
      variables() {
        return {
          reviewId: this.review.id,
          first: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceDuplicateGroupConnection(
          data.referenceDuplicateGroupsReviewNeeded
        ),
      skip() {
        return !this.showReviewNeededOnly;
      },
    },
  },
  computed: {
    emptyStateMessage() {
      if (this.showAutoResolvedOnly) {
        return 'No auto-resolved duplicates could be found.';
      }
      if (this.showReviewedOnly) {
        return 'No duplicates resolved by the team could be found.';
      }
      if (this.showReviewNeededOnly) {
        return 'No duplicates are awaiting review.';
      }
      return 'References resolved as duplicates will appear here.';
    },
    showLoadingState() {
      return !this.currentConnection;
    },
    showLoadingIndicator() {
      return (
        !this.showLoadingState && this.duplicateGroups.length > 0
      );
    },
    resolvedDuplicatesCount() {
      return this.review?.resolvedDuplicatesCount ?? 0;
    },
    potentialDuplicatesCount() {
      return this.review?.potentialDuplicatesCount ?? 0;
    },
    hasNoDuplicatesToShow() {
      return (
        this.resolvedDuplicatesCount === 0 &&
        this.potentialDuplicatesCount === 0
      );
    },
    listTotalCount() {
      return this.showReviewNeededOnly
        ? this.potentialDuplicatesCount
        : this.resolvedDuplicatesCount;
    },
    duplicateGroups() {
      return this.currentConnection?.nodes() ?? [];
    },
    hasMoreDuplicateGroups() {
      return this.currentConnection?.pageInfo?.hasNextPage ?? false;
    },
    currentConnection() {
      if (this.showAutoResolvedOnly) {
        return this.autoResolvedDuplicateGroups;
      }
      if (this.showReviewedOnly) {
        return this.reviewedDuplicateGroups;
      }
      if (this.showReviewNeededOnly) {
        return this.reviewNeededDuplicateGroups;
      }
      return this.allDuplicateGroups;
    },
    currentQuery() {
      if (this.showAutoResolvedOnly) {
        return this.$apollo.queries.autoResolvedDuplicateGroups;
      }
      if (this.showReviewedOnly) {
        return this.$apollo.queries.reviewedDuplicateGroups;
      }
      if (this.showReviewNeededOnly) {
        return this.$apollo.queries.reviewNeededDuplicateGroups;
      }
      return this.$apollo.queries.allDuplicateGroups;
    },
    showAutoResolvedOnly() {
      return (
        this.selectedFilters.length === 1 &&
        this.selectedFilters.includes(DuplicateFilterBy.AUTO_RESOLVED)
      );
    },
    showReviewedOnly() {
      return (
        this.selectedFilters.length === 1 &&
        this.selectedFilters.includes(DuplicateFilterBy.REVIEWED)
      );
    },
    showReviewNeededOnly() {
      return (
        this.selectedFilters.length === 1 &&
        this.selectedFilters.includes(DuplicateFilterBy.REVIEW_NEEDED)
      );
    },
  },
  destroyed() {
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'referenceDuplicateGroups',
    });
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'referenceDuplicateGroupsAutoResolved',
    });
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'referenceDuplicateGroupsResolvedByUser',
    });
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'referenceDuplicateGroupsReviewNeeded',
    });
  },
  methods: {
    ...mapActions({
      resolveDuplicates: 'reference/resolveDuplicates',
    }),
    onMarkAsNotDuplicate({ mainCopy, reference }) {
      this.resolveDuplicates({
        forReferenceId: mainCopy.id,
        keepReferenceIds: [reference.id],
      });
    },
    onMarkAsDuplicate({ mainCopy, reference }) {
      this.resolveDuplicates({
        forReferenceId: mainCopy.id,
        removeReferenceIds: [reference.id],
      });
    },
    async onLoadMore() {
      if (this.loadingMore || this.showLoadingState) return;
      if (!this.hasMoreDuplicateGroups) return;

      this.loadingMore = true;
      try {
        await this.currentQuery.fetchMore({
          variables: {
            first: PAGE_SIZE,
            after: this.currentConnection.pageInfo.endCursor,
          },
          updateQuery: appendNextResults,
        });
      } finally {
        this.loadingMore = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$filters-height: 36px;

.duplicates-tab-duplicates {
  height: calc(100% - #{$filters-height});
}
</style>
